import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import ArtworkSelectionModal from '../components/ArtworkSelectionModal';
import ComparisonResult from '../components/ComparisonResult';
import ArtAnalysisHotspots from '../components/ArtAnalysisHotspots';
import { saveArtComparisonActivity } from '../utils/activityService';
import { backendConfig } from '../config/backendConfig';
import { getCache, setCache, createCacheKey, clearCache } from '../utils/cache';
import { enqueueAndPoll } from '../utils/jobPolling';

const ArtHistoryCompare = () => {
  // Use backendConfig for proper production URL handling
  const API_BASE = backendConfig.baseUrl;
  
  console.log('[ArtHistoryCompare] API_BASE configured as:', API_BASE);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user came from mentor dashboard
  const isFromMentorDashboard = user?.accountType === 'mentor' && user?.mentorType === 'single';
  
  console.log('[ArtHistoryCompare] User context:', {
    accountType: user?.accountType,
    mentorType: user?.mentorType,
    isFromMentorDashboard
  });
  const [selectedArtwork1, setSelectedArtwork1] = useState(null);
  const [selectedArtwork2, setSelectedArtwork2] = useState(null);
  const [showModal1, setShowModal1] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [isComparing, setIsComparing] = useState(false);
  const [error, setError] = useState(null);
  const [visibleSection, setVisibleSection] = useState(null); // 'similarities' | 'differences' | 'insight'
  const [expandedPoint, setExpandedPoint] = useState(null); // Track which point is expanded (0, 1, 2, 3)
  const [uploadPublic, setUploadPublic] = useState(false);
  const [activityFile, setActivityFile] = useState(null);
  
  // Progressive loading states
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Art movement options for filtering
  const artMovements = [
    'Romanticism', 'Impressionism', 'Expressionism', 'Realism', 'Surrealism',
    'Abstract Expressionism', 'Baroque', 'Contemporary', 'Abstract Art', 'Post-Impressionism',
    'Art Nouveau', 'Conceptual Art', 'Minimalism', 'Pop Art', 'Academic Art',
    'Symbolism', 'Art Informel', 'Cubism', 'Post-Painterly Abstraction', 'Neoclassicism',
    'Pointillism', 'Fauvism', 'Neo-Suprematism',
    'Rococo', 'Neo-Expressionism', 'Op Art', 'Post-Minimalism', 'Dutch Golden Age',
    'Concrete Art (Concretism)', 'Modernism', 'Socialist Realism', 'Contemporary Realism', 'Naive Art', 'Spectralism',
    'Mannerism (Late Renaissance)', 'Northern Renaissance', 'Naïve Art (Primitivism)',
    'Feminist Art', 'Social Realism', 'Magic Realism', 'Neo-Minimalism (Neo-Geo)',
    'Constructivism', 'Kitsch', 'High Renaissance', 'Outsider art (Art brut)',
    'Early Renaissance', 'Orientalism', 'Avant-garde', 'Tachisme', 'Neo-Dada',
    'Photorealism (Super-Realism, Hyper-Realism)', 'Street art', 'Shōwa period (1926–1989)',
    'Naturalism', 'Modernismo', 'Light and Space', 'Futurism', 'Kinetic art',
    'Environmental Art (Land art)', 'Dada', 'Neo-Pop Art', 'Edo period (1603–1867)',
    'Neo-Impressionism', 'Art Deco', 'Soviet Nonconformist Art', 'Taishō period (1912–1926)',
    'Harlem Renaissance (New Negro Movement)', 'Performance Art', 'Native Art',
    'Postcolonial art', 'Arte Povera', 'Neo-Figurative Art', 'Nouveau Réalisme',
    'Relational Art', 'Regionalism', 'Muralism', 'New European Painting', 'Pictorialism',
    'New Realism (American Realism)', 'Gothic Art', 'Tonalism', 'De Stijl (Neoplasticism)',
    'Precisionism', 'Transavantgarde', 'Meiji Period (1868–1912)', 'Arts and Crafts',
    'Neo-Romanticism', 'Spatialism', 'Classical Realism', 'Proto Renaissance',
    'New Objectivity (Neue Sachlichkeit)', 'Cubo-Futurism', 'Metaphysical art', 'CyberArt',
    'Republic of China (1912–1949)', 'Romanesque Art', 'Aestheticism', 'Junk Art',
    'Provisional Painting (New Casualism)', 'Ming Dynasty (1368–1644)', 'Young Poland',
    'Fantasy Art', 'Suprematism', 'Haute Pâte (Matter Painting)', 'Neo-Concretism',
    'Fiber Art', 'Existential Art', 'New Generation Sculpture', 'New Image Painting',
    'Neo-Orthodoxism', 'Neo-Surrealism', 'Digital Art', 'Indigenism', 'Institutional Critique',
    'Secession', 'Orphism (Simultanism)', 'Lettrism', 'Abstract Illusionism', 'Mail Art',
    'P&D (Pattern and Decoration)', 'Lowbrow Art', 'Tang Dynasty (618–907)', 'Byzantine Art',
    'Vorticism', 'Korean Informel', 'Maximalism', 'Northern Song Dynasty (960–1126)',
    'Qing Dynasty (1644–1912)', 'Muromachi period (1392–1573)', 'Safavid Period (before 1600)',
    'Dansaekhwa (Korean Monochrome Painting)', 'Excessivism', 'New Ink Art', 'New media art',
    'Purism', 'Indian Space Painting', 'Poster Art Realism', 'Stuckism', 'Yuan Dynasty (1271–1368)',
    'New Culture Movement (1915-1926)', 'Ottoman Period (after 1600)', 'People\'s Republic of China (1949-present)',
    'Classical Period (480 - 323 BC)', 'Hellenistic Period (323-31 BC)', 'Rayonism', 'Synchromism',
    'Viennese Actionism', 'Pittura Colta', 'Five Dynasties and Ten Kingdoms (907–960)',
    'Timurid Period (c.1370–1507)', 'Ottoman Period (before 1600)', 'Safavid and Qajar Periods (after 1600)',
    'Joseon Dynasty', 'Ero guro', 'Sots Art', 'Archaic Period (700-480 BC)', 'Medieval Art',
    'Coptic art', 'Black Arts Movement', 'Pre-Romanesque Art', 'Analytical Realism',
    'Transautomatism', 'Funk Art', 'Confessional Art', 'Southern Song Dynasty (1127–1279)',
    'Azuchi-Momoyama period (1573–1603)', 'Abbasid Period (750–1258)', 'Ilkhanid Period (1256–1353)',
    'Süleyman the Magnificent Period (1520–1566)', 'Mughal Period (after 1600)', 'Art Singulier',
    'Incoherents', 'Neo-Conceptualism', 'Internet art (Net art)', 'Postinternet', 'Roman Period (30 BC - 640 AD)',
    'New Medievialism', 'Geometric Period (c.900 - c.700 BC)', 'Early Dynastic Period (3150 – 2686 BC)',
    'Old Kingdom (2686–2181 BC)', '1st Intermediate Period (2181–2055 BC)', 'Middle Kingdom (2055–1650 BC)',
    '2nd Intermediate Period (1650–1550 BC)', 'New Kingdom (1550–1069 BC)', 'Amarna Period (1351 - 1334 BC)',
    '3rd Intermediate Period (1069–664 BC)', 'Late Period (664–332 BC)', 'Ptolemaic Period (332–30 BC)',
    'Yoruba Art', 'Futuretech Art', 'Folk art', 'net.art', 'Post-classic (c.900-1580)',
    'Chicano art movement'
  ];

  // Art form options
  const artForms = [
    'Oil on canvas', 'Watercolor', 'Acrylic on canvas', 'Tempera', 'Fresco',
    'Gouache', 'Ink on paper', 'Charcoal', 'Pencil', 'Pastel',
    'Mixed media', 'Digital', 'Photography', 'Sculpture - Bronze', 'Sculpture - Marble',
    'Sculpture - Wood', 'Sculpture - Stone', 'Sculpture - Clay', 'Print - Etching',
    'Print - Lithograph', 'Print - Woodcut', 'Print - Screen print', 'Installation',
    'Performance', 'Video', 'Textile', 'Ceramic', 'Glass', 'Metal',
    'Paper', 'Plastic', 'Found objects', 'Collage', 'Assemblage'
  ];

  // Artwork type options (same as Art History Appreciation)
  const artworkTypes = [
    'Portrait', 'Self-portrait', 'Landscape', 'Seascape', 'Cityscape', 'Urban scene',
    'Still life', 'Genre scene', 'Historical', 'Mythological', 'Religious', 'Sacred',
    'Allegorical', 'Symbolic', 'Abstract', 'Non-representational', 'Figurative',
    'Narrative', 'Documentary', 'Political', 'Social commentary', 'Satirical',
    'Decorative', 'Ornamental', 'Architectural', 'Interior', 'Exterior',
    'Nature study', 'Botanical', 'Zoological', 'Anatomical', 'Scientific',
    'Fantasy', 'Surreal', 'Dreamlike', 'Imaginative', 'Fictional',
    'Realistic', 'Naturalistic', 'Photorealistic', 'Hyperrealistic',
    'Expressionistic', 'Emotional', 'Psychological', 'Introspective',
    'Minimalist', 'Geometric', 'Mathematical', 'Conceptual', 'Theoretical'
  ];

  // Time period options
  const timePeriods = [
    'Ancient (Before 500 AD)', 'Medieval (500-1400)', 'Renaissance (1400-1600)',
    'Baroque (1600-1750)', 'Neoclassical (1750-1850)', 'Romantic (1800-1850)',
    'Realism (1850-1900)', 'Impressionism (1860-1900)', 'Post-Impressionism (1880-1910)',
    'Modern (1900-1970)', 'Contemporary (1970-Present)'
  ];

  const handleArtworkSelect = (artwork, artworkNumber) => {
    if (artworkNumber === 1) {
      setSelectedArtwork1(artwork);
      setShowModal1(false);
      // Clear comparison results when artwork 1 is changed
      setComparisonResult(null);
      setVisibleSection(null);
      setExpandedPoint(null);
    } else {
      setSelectedArtwork2(artwork);
      setShowModal2(false);
      // Clear comparison results when artwork 2 is changed
      setComparisonResult(null);
      setVisibleSection(null);
      setExpandedPoint(null);
    }
  };

  const handlePointClick = (pointIndex) => {
    // If clicking the same point, collapse it; otherwise expand the new point
    setExpandedPoint(expandedPoint === pointIndex ? null : pointIndex);
  };

  const handleCompare = async () => {
    if (!selectedArtwork1 || !selectedArtwork2) {
      setError('Please select both artworks to compare');
      return;
    }

    if (selectedArtwork1.id === selectedArtwork2.id) {
      setError('Please select two different artworks to compare');
      return;
    }

    setIsComparing(true);
    setError(null);
    setComparisonResult(null);
    setVisibleSection(null);

    try {
      console.log('[ArtHistoryCompare] Starting comparison...');
      setComparisonLoading(true);

      // Normalize artwork payload to ensure backend shape
      const getImageUrl = (a) =>
        a?.imageUrl || a?.originalImageUrl || a?.coverUrl || a?.image || a?.images?.[0]?.url || a?.image_url || '';

      const normalize = (x) => x && ({
        imageUrl: getImageUrl(x),
        title: x.title || 'Untitled',
        artistName: x.artistName || x.artist || 'Unknown',
        artMovement: x.artMovement || x.movement || 'Unknown',
        description: x.description || ''
      });

      const artwork1 = normalize(selectedArtwork1);
      const artwork2 = normalize(selectedArtwork2);

      if (!artwork1?.imageUrl || !artwork2?.imageUrl) {
        console.warn('[ArtHistoryCompare] Missing imageUrl in payload', { artwork1, artwork2, selectedArtwork1, selectedArtwork2 });
        setError('Please select two artworks with images to compare.');
        setComparisonLoading(false);
        return;
      }

      const payload = { artwork1, artwork2 };
      console.log('[ArtHistoryCompare] Request payload:', payload);

      // COST OPTIMIZATION: Use background workers with polling to reduce HTTP handler costs
      console.log('[ArtHistoryCompare] Enqueueing comparison job...');
      setComparisonLoading(true);

      const compareEndpoint = API_BASE.endsWith('/api')
        ? `${API_BASE}/compare/compare-artworks`
        : `${API_BASE}/api/compare/compare-artworks`;

      const result = await enqueueAndPoll(
        compareEndpoint,
        payload,
        {
          interval: 2000, // Poll every 2 seconds
          maxAttempts: 150, // Max 5 minutes
          onProgress: (status, job, attempts) => {
            let message = '';
            if (status === 'enqueued') message = 'Preparing artwork comparison...';
            else if (status === 'processing') message = `Comparing artworks (attempt ${attempts})...`;
            else if (status === 'completed') message = 'Comparison complete!';
            console.log('[ArtHistoryCompare] Job status:', status, message);
          }
        }
      );
      
      console.log('[ArtHistoryCompare] Comparison result received:', {
        hasSimilarities: !!(result?.similarities?.length),
        hasDifferences: !!(result?.differences?.length),
        hasInsight: !!result?.insight,
        hasActivity: !!result?.activity
      });
      setComparisonResult(result);
      setVisibleSection(null); // Don't auto-open any section, let user choose
      setExpandedPoint(null); // Clear any expanded point

      // Save art comparison to activity dashboard (for both mentors and students)
      if (result && user) {
        try {
          console.log('[ArtHistoryCompare] Saving art comparison to activity dashboard...');
          const activityId = await saveArtComparisonActivity({
            artwork1: selectedArtwork1,
            artwork2: selectedArtwork2,
            comparisonResult: result,
            title: `Comparing: ${selectedArtwork1?.title || 'Artwork 1'} & ${selectedArtwork2?.title || 'Artwork 2'}`,
            description: `Comparison between ${selectedArtwork1?.title || 'artwork 1'} and ${selectedArtwork2?.title || 'artwork 2'}`,
            metadata: {
              context: user.accountType === 'mentor' ? 'mentor' : 'student',
              mentorId: user.accountType === 'mentor' ? user.uid : null,
              studentId: user.accountType !== 'mentor' ? user.uid : null,
              className: typeof window !== 'undefined' ? (localStorage.getItem('mentorClass') || user.className || '') : (user.className || ''),
              section: typeof window !== 'undefined' ? (localStorage.getItem('mentorSection') || user.section || '') : (user.section || '')
            }
          });
          console.log('✅ Art comparison saved to activity dashboard with ID:', activityId);
          
          // Clear cache to force refresh (user's own activities)
          const cacheKey = createCacheKey('userActivities', { userId: user.uid }, user.uid);
          clearCache(cacheKey);
          console.log('✅ [ART HISTORY COMPARE] Cache cleared after save');
        } catch (activityError) {
          console.warn('⚠️ Failed to save art comparison to activity dashboard:', activityError);
        }
      }
    } catch (error) {
      console.error('[ArtHistoryCompare] Error during comparison:', error);
      console.error('[ArtHistoryCompare] Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack?.substring(0, 500)
      });
      
      let errorMessage = 'Failed to compare artworks. Please try again.';
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        errorMessage = 'Request timed out. The comparison is taking longer than expected. Please try again.';
      } else if (error.message.includes('Network error') || error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection and ensure the backend server is running.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      console.log('[ArtHistoryCompare] Comparison process finished, clearing loading states');
      setIsComparing(false);
      setComparisonLoading(false);
    }
  };

  const handleActivityFile = (e) => {
    setActivityFile(e.target.files[0]);
  };

  const handleActivityUpload = async () => {
    if (!activityFile) {
      alert('Please select a file to upload');
      return;
    }

    // Here you would implement the actual upload logic
    console.log('Uploading activity file:', activityFile.name, 'Public:', uploadPublic);
    alert('Activity upload functionality would be implemented here');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-200/10 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <div className="relative bg-gradient-to-r from-amber-200/90 via-amber-100/90 to-amber-50/90 backdrop-blur-sm text-amber-800 p-6 shadow-lg border-b border-amber-300/30">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-3xl font-bold text-black">
                The Art of Comparison
              </h1>
              <p className="text-black font-medium">Compare artworks and discover artistic connections</p>
            </div>
          </div>
          <button
            onClick={() => {
              // For students, navigate to My Art History Learning; for mentors, go back
              if (user && user.accountType !== 'mentor') {
                navigate('/my-art-history');
              } else {
                navigate(-1);
              }
            }}
            className="bg-white/80 hover:bg-white text-amber-700 px-4 py-2 rounded-lg font-semibold shadow-md transition-all duration-200 hover:shadow-lg"
          >
            ← Back
          </button>
        </div>
      </div>

      {/* Loading Overlay */}
      {(isComparing || comparisonLoading) && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl px-8 py-8 flex flex-col items-center mx-4 border-4 border-amber-300">
            <svg className="animate-spin h-12 w-12 text-amber-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <div className="text-amber-700 font-bold text-lg text-center mb-2">Artmosphere is comparing your artworks</div>
            <div className="text-amber-600 text-sm text-center">Please wait while we analyze the similarities and differences</div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Artwork Selection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Artwork 1 */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-amber-200/50">
            <h3 className="text-xl font-bold text-amber-800 mb-4">First Artwork</h3>
              {selectedArtwork1 ? (
                <div className="space-y-4">
                      <img
                        src={selectedArtwork1.imageUrl}
                        alt={selectedArtwork1.title}
                  className="w-full h-64 object-cover rounded-lg shadow-md"
                />
                <div>
                  <h4 className="font-bold text-lg text-gray-800">{selectedArtwork1.title}</h4>
                    <p className="text-gray-600">{selectedArtwork1.artistName}</p>
                  <p className="text-sm text-amber-600">{selectedArtwork1.artMovement}</p>
                    </div>
                <button
                  onClick={() => setShowModal1(true)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white py-2 px-4 rounded-lg font-semibold transition-colors"
                >
                  Change Artwork
                </button>
                </div>
              ) : (
                  <button
                    onClick={() => setShowModal1(true)}
                className="w-full h-64 border-2 border-dashed border-amber-300 rounded-lg flex items-center justify-center text-amber-600 hover:border-amber-400 hover:text-amber-700 transition-colors"
                  >
                <div className="text-center">
                  <div className="text-4xl mb-2">+</div>
                  <div className="font-semibold">Select First Artwork</div>
                </div>
                  </button>
                )}
              </div>

          {/* Artwork 2 */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-amber-200/50">
            <h3 className="text-xl font-bold text-amber-800 mb-4">Second Artwork</h3>
              {selectedArtwork2 ? (
                <div className="space-y-4">
                      <img
                        src={selectedArtwork2.imageUrl}
                        alt={selectedArtwork2.title}
                  className="w-full h-64 object-cover rounded-lg shadow-md"
                />
                <div>
                  <h4 className="font-bold text-lg text-gray-800">{selectedArtwork2.title}</h4>
                    <p className="text-gray-600">{selectedArtwork2.artistName}</p>
                  <p className="text-sm text-amber-600">{selectedArtwork2.artMovement}</p>
                    </div>
                <button
                  onClick={() => setShowModal2(true)}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white py-2 px-4 rounded-lg font-semibold transition-colors"
                >
                  Change Artwork
                </button>
                </div>
              ) : (
                  <button
                    onClick={() => setShowModal2(true)}
                className="w-full h-64 border-2 border-dashed border-amber-300 rounded-lg flex items-center justify-center text-amber-600 hover:border-amber-400 hover:text-amber-700 transition-colors"
                  >
                <div className="text-center">
                  <div className="text-4xl mb-2">+</div>
                  <div className="font-semibold">Select Second Artwork</div>
                </div>
              </button>
              )}
            </div>
          </div>

        {/* Compare Button or Analysis Buttons */}
        <div className="text-center mb-8">
            {!comparisonResult ? (
              <button
                onClick={handleCompare}
                disabled={!selectedArtwork1 || !selectedArtwork2 || isComparing}
              className={`px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 ${
                selectedArtwork1 && selectedArtwork2 && !isComparing
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isComparing ? 'Comparing...' : 'Compare Artworks'}
              </button>
            ) : (
            <div className="flex flex-wrap justify-center gap-6">
                <button
                  onClick={() => { setVisibleSection('similarities'); setExpandedPoint(null); }}
                className="group relative px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border-2 border-amber-400 hover:border-amber-500"
                >
                <span className="relative z-10 flex items-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Similarities
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                </button>
                <button
                  onClick={() => { setVisibleSection('differences'); setExpandedPoint(null); }}
                className="group relative px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border-2 border-orange-400 hover:border-orange-500"
                >
                <span className="relative z-10 flex items-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Differences
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                </button>
                <button
                  onClick={() => { setVisibleSection('insight'); setExpandedPoint(null); }}
                className="group relative px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 border-2 border-emerald-400 hover:border-emerald-500"
                >
                <span className="relative z-10 flex items-center gap-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Learning Insight
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                </button>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-100 border border-red-300 rounded-xl text-red-700 text-center">
              {error}
            </div>
          )}
        </div>

      {/* Results Modal */}
        {comparisonResult && visibleSection && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2">
          <div className="w-full max-w-7xl h-[95vh] bg-white rounded-2xl shadow-2xl border-4 border-amber-200 flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-amber-500 to-amber-400 rounded-t-2xl">
                <h3 className="text-white text-lg font-bold">
                  {visibleSection === 'similarities' && 'Similarities'}
                  {visibleSection === 'differences' && 'Differences'}
                  {visibleSection === 'insight' && 'Learning Insight'}
                </h3>
                <button
                  onClick={() => setVisibleSection(null)}
                  className="bg-white/90 hover:bg-white text-amber-600 rounded-full px-3 py-1 font-semibold shadow"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="px-3 py-3 flex-1 overflow-auto flex flex-col">
              {/* Note banner at the TOP with numbered buttons and details (Similarities/Differences) */}
              {(visibleSection === 'similarities' || visibleSection === 'differences') && (
                <div className={`mb-4 rounded-lg p-4 text-center border-2 ${
                  visibleSection === 'similarities'
                    ? 'bg-amber-100 border-amber-300'
                    : 'bg-orange-100 border-orange-300'
                }`}>
                  <p className={`text-sm font-medium mb-3 ${
                    visibleSection === 'similarities' ? 'text-amber-800' : 'text-orange-800'
                  }`}>
                    {visibleSection === 'similarities' ? '💡' : '🔍'} <strong>Try this first:</strong> Look at both artworks and try to {visibleSection === 'similarities' ? 'find similarities' : 'spot the differences'} yourself!
                  </p>
                  
                  {/* Numbered buttons for each similarity/difference */}
                  {(() => {
                    const items = visibleSection === 'similarities' 
                      ? (Array.isArray(comparisonResult?.similarities) ? comparisonResult.similarities : [comparisonResult?.similarities].filter(Boolean))
                      : (Array.isArray(comparisonResult?.differences) ? comparisonResult.differences : [comparisonResult?.differences].filter(Boolean));
                    
                    if (!items || items.length === 0) return null;
                    
                    return (
                      <div className="flex flex-wrap justify-center gap-2 mb-4">
                        {items.map((item, index) => (
                          <button
                            key={index}
                            onClick={() => handlePointClick(index)}
                            className={`px-4 py-2 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 ${
                              expandedPoint === index
                                ? visibleSection === 'similarities'
                                  ? 'bg-amber-600 shadow-lg scale-105'
                                  : 'bg-orange-600 shadow-lg scale-105'
                                : visibleSection === 'similarities'
                                  ? 'bg-amber-500 hover:bg-amber-600'
                                  : 'bg-orange-500 hover:bg-orange-600'
                            }`}
                          >
                            {index + 1}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  
                  {/* Display selected point or all points if none selected */}
                  <div className={`mt-4 mx-auto max-w-4xl text-left bg-white rounded-xl p-6 border-2 ${
                    visibleSection === 'similarities' ? 'border-amber-200' : 'border-orange-200'
                  } shadow-sm`}>
                    {(() => {
                      const items = visibleSection === 'similarities' 
                        ? (Array.isArray(comparisonResult?.similarities) ? comparisonResult.similarities : [comparisonResult?.similarities].filter(Boolean))
                        : (Array.isArray(comparisonResult?.differences) ? comparisonResult.differences : [comparisonResult?.differences].filter(Boolean));
                      
                      if (!items || items.length === 0) {
                        // Fallback: display as string if not array
                        const text = visibleSection === 'similarities' 
                          ? comparisonResult?.similarities 
                          : comparisonResult?.differences;
                        return (
                          <p className="text-gray-800 text-lg leading-relaxed whitespace-pre-line">
                            {text || ''}
                          </p>
                        );
                      }
                      
                      // If a specific point is expanded, show only that point
                      if (expandedPoint !== null && items[expandedPoint]) {
                        return (
                          <div>
                            <p className="text-gray-700 font-semibold mb-3 text-xl">
                              {visibleSection === 'similarities' ? 'Similarity' : 'Difference'} {expandedPoint + 1}:
                            </p>
                            <p className="text-gray-800 text-lg leading-relaxed whitespace-pre-line">
                              {items[expandedPoint]}
                            </p>
                          </div>
                        );
                      }
                      
                      // Initial state: show hint to click a button
                      return (
                        <div className="text-center py-8">
                          <p className="text-gray-600 text-lg mb-4">
                            👆 Click on a numbered button above to explore each {visibleSection === 'similarities' ? 'similarity' : 'difference'} in detail!
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Conditional Layout: Different for Learning Insight vs Similarities/Differences */}
              {visibleSection === 'insight' ? (
                // Learning Insight Layout - Smaller artworks, more space for activity
                <>
                  {/* Artworks Section - Top (Smaller for Learning Insight) */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-2">
                    {/* Artwork 1 */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm">
                      <img src={selectedArtwork1.imageUrl} alt={selectedArtwork1.title} className="w-full object-contain" style={{ height: '300px' }} />
                      <div className="p-3 bg-gray-50">
                        <div className="text-lg font-bold text-gray-800 mb-1">{selectedArtwork1.title}</div>
                        <div className="text-sm text-gray-600 mb-1">{selectedArtwork1.artistName}</div>
                        <div className="text-xs text-amber-600 font-medium bg-amber-100 px-2 py-1 rounded-full inline-block">
                          {selectedArtwork1.artMovement}
                        </div>
                      </div>
                    </div>
                    
                    {/* Artwork 2 */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm">
                      <img src={selectedArtwork2.imageUrl} alt={selectedArtwork2.title} className="w-full object-contain" style={{ height: '300px' }} />
                      <div className="p-3 bg-gray-50">
                        <div className="text-lg font-bold text-gray-800 mb-1">{selectedArtwork2.title}</div>
                        <div className="text-sm text-gray-600 mb-1">{selectedArtwork2.artistName}</div>
                        <div className="text-xs text-amber-600 font-medium bg-amber-100 px-2 py-1 rounded-full inline-block">
                          {selectedArtwork2.artMovement}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Analysis Section - Bottom (Compact for Learning Insight) */}
                  <div className="bg-amber-50 rounded-xl p-3 border-2 border-amber-200 flex-1 overflow-auto">
                    {visibleSection === 'insight' && comparisonResult.insight && (
                      <div className="space-y-3">
                        <p className="text-gray-800 leading-relaxed text-lg whitespace-pre-wrap">{comparisonResult.insight}</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                // Similarities/Differences Layout - Larger artworks with numbered overlay points
                <>
                  {/* Artworks Section - Top (Larger for Similarities/Differences) */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                    {/* Artwork 1 */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm relative">
                      <div className="relative" style={{ height: '500px' }}>
                        <img src={selectedArtwork1.imageUrl} alt={selectedArtwork1.title} className="w-full h-full object-contain" />
                        {/* Numbered overlay removed; buttons now in the note box above */}
                      </div>
                      <div className="p-3 bg-gray-50">
                        <div className="text-lg font-bold text-gray-800 mb-1">{selectedArtwork1.title}</div>
                        <div className="text-sm text-gray-600 mb-1">{selectedArtwork1.artistName}</div>
                        <div className="text-xs text-amber-600 font-medium bg-amber-100 px-2 py-1 rounded-full inline-block">
                          {selectedArtwork1.artMovement}
                        </div>
                      </div>
                    </div>
                    
                    {/* Artwork 2 */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm relative">
                      <div className="relative" style={{ height: '500px' }}>
                        <img src={selectedArtwork2.imageUrl} alt={selectedArtwork2.title} className="w-full h-full object-contain" />
                        {/* Numbered overlay removed; buttons now in the note box above */}
                      </div>
                      <div className="p-3 bg-gray-50">
                        <div className="text-lg font-bold text-gray-800 mb-1">{selectedArtwork2.title}</div>
                        <div className="text-sm text-gray-600 mb-1">{selectedArtwork2.artistName}</div>
                        <div className="text-xs text-amber-600 font-medium bg-amber-100 px-2 py-1 rounded-full inline-block">
                          {selectedArtwork2.artMovement}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* No bottom analysis for similarities/differences; details shown in the top note box */}
                </>
              )}
              </div>
            </div>
          </div>
        )}

      {/* Artwork Selection Modals */}
      {showModal1 && (
        <ArtworkSelectionModal
          isOpen={showModal1}
          onClose={() => setShowModal1(false)}
          onSelect={(artwork) => handleArtworkSelect(artwork, 1)}
          selectedArtwork={selectedArtwork1}
          otherSelectedArtwork={selectedArtwork2}
          artMovements={artMovements}
          artForms={artForms}
          artworkTypes={artworkTypes}
        />
      )}

      {showModal2 && (
        <ArtworkSelectionModal
          isOpen={showModal2}
          onClose={() => setShowModal2(false)}
          onSelect={(artwork) => handleArtworkSelect(artwork, 2)}
          selectedArtwork={selectedArtwork2}
          otherSelectedArtwork={selectedArtwork1}
          artMovements={artMovements}
          artForms={artForms}
          artworkTypes={artworkTypes}
        />
      )}
    </div>
  );
};

export default ArtHistoryCompare;